import React, { useEffect, useState } from 'react'
import Head from 'next/head'
import styled from '@emotion/styled'
import { useFeatureValue, useGrowthBook } from '@growthbook/growthbook-react'
import { mq } from '../helpers'
import { HomeWrapper } from '../components/home/styles'

// 2023 homepage sections. Previously composed inside <HomePage2023 />, they are
// assembled directly here so the hero/mission block at the top of the page can be
// A/B-tested independently of the rest of the page (see showHero2026 below).
import Hero2023 from '../components/home/2023/HeroSection'
import MissionSection from '../components/home/2023/MissionSection'
import ServicesSection from '../components/home/2023/ServicesSection'
import HowToBookSection from '../components/home/2023/HowToBookSection'
import OurBeautyPros from '../components/home/OurBeautyPros'
import LocationsSection from '../components/home/2023/LocationsSection'
import RatingsSection from '../components/home/2023/RatingsSection'
import PressSection from '../components/home/2023/PressSection'

// 2026 hero variant — the ONLY 2026 section in this experiment. The rest of the
// 2026 work (HomePage2026.js and its sections) stays parked for a likely future
// full-page test and is intentionally not imported here.
import Hero2026 from '../components/home/2026/HeroSection'

import 'swiper/css'
import 'swiper/css/pagination'

// Re-exported for external consumers (landing/texas/Locations.js, events/Locations.js)
// which historically imported `locations` from this page module.
export { locations } from '../components/home/2023/data'

// While GrowthBook resolves which hero to show, reserve the hero's vertical space so
// the page doesn't paint one hero and then swap to the other (flash). Mirrors the
// TALLER of the two heroes — the 2023 video hero (594px on mobile, 16/9 auto above
// 768px) — so content below never jumps up when the shorter 2026 variant wins.
const HeroPlaceholder = styled.div`
  width: 100%;
  max-width: 1600px;
  /* Reserve the taller hero at every breakpoint so neither variant shifts layout.
     Base = 2023 control (594px mobile, 16/9 auto above 768). The min-heights floor it
     at the 2026 variant's fixed heights (480 ≤1024, 600 above), where the 16/9 curve
     would otherwise dip below them (~769px) and cause a downward shift. */
  height: 594px;
  aspect-ratio: 16 / 9;

  ${mq('min', '769px')} {
    height: auto;
    min-height: 480px;
  }

  ${mq('min', '1024px')} {
    min-height: 600px;
  }
`

const Home = () => {
  // A/B experiment: gate ONLY the hero/top-of-page. Control (false) = 2023 hero +
  // mission; variant (true) = 2026 hero (its own quote/CTA block is built in).
  const growthbook = useGrowthBook()
  const showHero2026 = useFeatureValue('show-new-hero', false)
  // Until GrowthBook has loaded features, useFeatureValue returns the default (false),
  // which would render the control hero and then flip once the experiment resolves.
  // Hold an empty reserved space until the flag is known.
  //
  // Fallback: growthbook.ready only flips true once features actually load — it stays
  // false forever if the fetch fails (e.g. cdn.growthbook.io blocked by an ad blocker).
  // Without this, those users would see a blank hero forever, so after a short wait we
  // reveal the hero anyway (the flag defaults to false = control, the safe hero).
  const [flagTimedOut, setFlagTimedOut] = useState(false)
  const gbReady = Boolean(growthbook?.ready)
  useEffect(() => {
    // Only arm the fallback while GrowthBook is not ready; once readiness arrives the
    // effect cleanup clears the pending timer, so we never fire a needless re-render.
    if (gbReady) {
      return undefined
    }
    const timer = setTimeout(() => setFlagTimedOut(true), 2500)
    return () => clearTimeout(timer)
  }, [gbReady])
  const heroReady = gbReady || flagTimedOut

  return (
    <HomeWrapper>
      <Head>
        <title>GLAMSQUAD | In-Home Hair, Makeup & Nail Services</title>
        <meta name="description" content="Book in-home hair, makeup, and nail services from licensed GLAMSQUAD pros. Beauty delivered to your door in 15+ cities, 365 days a year." />
        <link rel='canonical' href='https://glamsquad.com' />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="GLAMSQUAD | In-Home Hair, Makeup & Nail Services" />
        <meta property="og:description" content="Book in-home hair, makeup, and nail services from licensed GLAMSQUAD pros. Beauty delivered to your door in 15+ cities, 365 days a year." />
        <meta property="og:url" content="https://glamsquad.com" />
        <meta property="og:image" content="https://glamsquad.com/images/new-gs-wordmark-logo.svg" />
        {/* Poster of the 2023 video hero (control arm of the hero A/B test). Both
            heroes resolve client-side after GrowthBook, so preload from the SSR'd
            page head to have the poster cached before the component mounts. Lives
            here (not _document.js) so only the homepage pays for it — globally it
            competed with every other page's LCP image. */}
        <link rel="preload" as="image" href="/home/hero-video-first-frame.jpg" fetchPriority="high" />
      </Head>

      {/* A/B: hold empty reserved space until the flag resolves, then 2026 hero
          (variant) OR 2023 hero + mission (control) — avoids a hero flash. */}
      {!heroReady ? (
        <HeroPlaceholder aria-hidden />
      ) : showHero2026 ? (
        <Hero2026 />
      ) : (
        <>
          <Hero2023 />
          <MissionSection />
        </>
      )}

      {/* Shared page body (2023) — identical for both variants. */}
      <ServicesSection />
      <HowToBookSection />
      <OurBeautyPros />
      <LocationsSection />
      <RatingsSection />
      <PressSection />
    </HomeWrapper>
  )
}

export default Home
